import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

/**
 * A value in a table cell that is also its own picker.
 *
 * The chip is the button — no hover-revealed icon, which a touch screen can
 * never show. Tapping it opens a styled list rather than the browser's native
 * dropdown: that one is drawn by the OS, looks different on every device, and
 * cannot be given the app's own type, colour or spacing.
 *
 * The list is rendered in a portal and placed against the viewport by hand.
 * Both are needed here: the cell sits inside `.card { overflow:hidden }` and a
 * table that scrolls sideways, either of which would clip an in-flow panel.
 *
 * Props:
 *   options  [{ value, label, hint?, dot? }]
 *   onPick   (value) => void
 *   children the chip itself
 */
export default function ChipPicker({
  children, options = [], onPick, title, busy = false, ariaLabel, header,
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const [active, setActive] = useState(0);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(Math.max(r.width, 208), window.innerWidth - 16);
    const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    // Open upwards when the room below would cut the list short.
    const up = below < 180 && above > below;
    setBox({
      left,
      width,
      top: up ? undefined : r.bottom + 4,
      bottom: up ? window.innerHeight - r.top + 4 : undefined,
      maxHeight: Math.max(140, up ? above : below),
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const away = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, options.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter' && options[active]) { e.preventDefault(); onPick(options[active].value); setOpen(false); }
    };
    const follow = () => place();
    document.addEventListener('mousedown', away);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => {
      document.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
    };
  }, [open, place, options, active, onPick]);

  const toggle = () => {
    if (busy) return;
    if (!open) { place(); setActive(0); }
    setOpen((o) => !o);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title={title}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={busy}
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        // A floor on both sides so even an empty value stays a thumb-sized
        // target; the desk machines get the tighter row back at `lg`.
        className={`relative -mx-1 inline-flex min-h-[2rem] min-w-[3rem] cursor-pointer items-center gap-1 rounded px-1
                    text-left ring-1 ring-transparent transition-colors
                    hover:bg-surface-hover hover:ring-line-strong
                    focus-visible:ring-2 focus-visible:ring-brand-400
                    lg:min-h-[1.75rem] lg:min-w-0
                    ${busy ? 'opacity-50' : ''} ${open ? 'bg-surface-hover ring-line-strong' : ''}`}
      >
        {children}
        <ChevronDown
          size={11}
          className={`shrink-0 text-content-subtle transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && box && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed', left: box.left, top: box.top, bottom: box.bottom, width: box.width,
          }}
          className="z-[70] overflow-hidden rounded-lg border border-line bg-surface-raised shadow-pop animate-slide-up"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {header && (
            <p className="border-b border-line px-3 py-1.5 text-2xs font-bold uppercase tracking-wider text-content-subtle">
              {header}
            </p>
          )}
          <ul role="listbox" style={{ maxHeight: Math.min(280, box.maxHeight) }} className="overflow-y-auto py-1">
            {options.length === 0 && (
              <li className="px-3 py-2 text-xs text-content-subtle">Nothing to choose.</li>
            )}
            {options.map((o, i) => (
              <li key={o.value} role="option" aria-selected={o.selected || false}>
                <button
                  type="button"
                  className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors
                              ${i === active ? 'bg-surface-hover' : 'hover:bg-surface-hover'}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={(e) => { e.stopPropagation(); onPick(o.value); setOpen(false); }}
                >
                  {o.dot && <span className={`h-2 w-2 shrink-0 rounded-full ${o.dot}`} />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-content">{o.label}</span>
                    {o.hint && <span className="block truncate text-2xs text-content-subtle">{o.hint}</span>}
                  </span>
                  {o.selected && <Check size={14} className="shrink-0 text-brand-600" />}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
